import {
  LOCALE_COOKIE,
  DEFAULT_LANGUAGE,
  SUPPORTED_COUNTRIES,
  COUNTRY_LANGUAGES,
} from '@src/config/localisation/locale-config.constants'
import { shouldEnableI18nClient } from '@src/server-side/utils/i18n-preview.utils'

import isBrowser from '../../src/quince-site-common/src/utils/isBrowser'
import getCookie from '../quince-site-common/src/utils/getCookie'

/**
 * Storage options for managing localized and global data
 * @property isGlobal - Determines how the key is stored and accessed:
 *   - true: Uses original key (backward compatible, shared across all languages) - DEFAULT
 *   - false: Uses localized key format (language-specific data)
 * @property includeLangInKey - Determines the prefix format:
 *   - false: Uses only country as prefix (e.g., "US:key", "CA:key") - DEFAULT
 *   - true: Uses full locale as prefix (e.g., "en-us:key", "fr-CA:key")
 * @property removeAllLocalized - When true, removes all localized variations of the key (original, country-prefixed, and locale-prefixed)
 */
interface StorageOptions {
  isGlobal?: boolean
  includeLangInKey?: boolean
  removeAllLocalized?: boolean
}

/**
 * Simple enhanced storage class that adds locale support when needed
 *
 * Key Format:
 * - Global keys (isGlobal=true): originalKey (unchanged for backward compatibility) - DEFAULT
 * - Localized keys (isGlobal=false):
 *   - includeLangInKey=false: {country}:originalKey (e.g., US:userPrefs, CA:userPrefs) - DEFAULT
 *   - includeLangInKey=true: {locale}:originalKey (e.g., en-us:userPrefs, fr-CA:userPrefs)
 */
class EnhancedStorage {
  private isI18nEnabled: boolean

  constructor(isI18nEnabled: boolean) {
    this.isI18nEnabled = isI18nEnabled
  }

  private getLocalizedKey(key: string, options: StorageOptions = {}): string {
    const { isGlobal = true, includeLangInKey = false } = options

    // If global or i18n disabled, use original key (backward compatible)
    if (isGlobal || !this.isI18nEnabled) {
      return key
    }

    // For localized data, get the current locale
    const locale = getCookie(LOCALE_COOKIE) ?? DEFAULT_LANGUAGE

    if (includeLangInKey) {
      // Use full locale as prefix (e.g., "en-us:key", "fr-CA:key")
      return `${locale}:${key}`
    } else {
      // Use only country as prefix (e.g., "US:key", "CA:key")
      // eslint-disable-next-line i18next/no-literal-string
      const country = locale.split('-')[1] ?? 'us' // Extract country from locale, fallback to US
      return `${country}:${key}`
    }
  }

  /**
   * Retrieves an item from storage with optional locale support
   * @param key - The key to retrieve
   * @param options - Storage options (optional)
   * @param options.isGlobal - true: use original key, false: use localized key format (default: true)
   * @param options.includeLangInKey - false: use country prefix (e.g., "US:key"), true: use full locale prefix (e.g., "en-us:key") (default: false)
   * @returns The stored value or null if not found
   *
   * @example
   * // Global data (default behavior)
   * const authToken = storage.getItem('authToken')
   * // Looks for: "authToken"
   * // If i18n disabled and not found: falls back to "us:authToken"
   *
   * // Country-specific data
   * const preferences = storage.getItem('userPrefs', { isGlobal: false })
   * // Looks for: "US:userPrefs" or "CA:userPrefs"
   * // For US region: falls back to "userPrefs" if "US:userPrefs" not found
   *
   * // Full locale-specific data
   * const preferences = storage.getItem('userPrefs', { isGlobal: false, includeLangInKey: true })
   * // Looks for: "en-us:userPrefs" or "fr-CA:userPrefs"
   */
  getItem<T>(key: string, options: StorageOptions = {}): T | null {
    if (!isBrowser()) return null

    try {
      const { isGlobal = true } = options
      const storageKey = this.getLocalizedKey(key, options)
      let item = localStorage.getItem(storageKey)

      // If isGlobal is false and we're in US region, add fallback to original key
      if (!item && !isGlobal && this.isI18nEnabled) {
        const locale = getCookie(LOCALE_COOKIE)
        const country = locale?.split('-')[1]

        // Only apply fallback for US region
        if (country && country.toLowerCase() === 'us') {
          item = localStorage.getItem(key)
          if (item) {
            try {
              localStorage.setItem(`us:${key}`, item)
              localStorage.removeItem(key)
            } catch (error) {
              // If migration fails, still return the item
              console.error('Error during migration:', error)
            }
          }
        }
      }

      // Reverse fallback: if i18n is disabled and we don't find the original key, check for US localized key
      if (!item && !this.isI18nEnabled) {
        item = localStorage.getItem(`us:${key}`)
        if (item) {
          try {
            localStorage.setItem(key, item)
            localStorage.removeItem(`us:${key}`)
          } catch (error) {
            // If migration fails, still return the item
            console.error('Error during migration:', error)
          }
        }
      }

      return item as T | null
    } catch (error) {
      console.error('Error getting item from storage:', error)
      return null
    }
  }

  /**
   * Stores an item with optional locale support
   * @param key - The key to store
   * @param value - The value to store
   * @param options - Storage options (optional)
   * @param options.isGlobal - true: use original key, false: use localized key format (default: true)
   * @param options.includeLangInKey - false: use country prefix (e.g., "US:key"), true: use full locale prefix (e.g., "en-us:key") (default: false)
   *
   * @example
   * // Global data (default behavior)
   * storage.setItem('authToken', token)
   * // Stores as: "authToken"
   *
   * // Country-specific data
   * storage.setItem('userPrefs', preferences, { isGlobal: false })
   * // Stores as: "US:userPrefs" or "CA:userPrefs"
   *
   * // Full locale-specific data
   * storage.setItem('userPrefs', preferences, { isGlobal: false, includeLangInKey: true })
   * // Stores as: "en-us:userPrefs" or "fr-CA:userPrefs"
   */
  setItem(key: string, value: string, options: StorageOptions = {}): void {
    if (!isBrowser()) return

    try {
      const { isGlobal = true } = options
      const storageKey = this.getLocalizedKey(key, options)
      localStorage.setItem(storageKey, value)

      if (!isGlobal && this.isI18nEnabled) {
        const locale = getCookie(LOCALE_COOKIE) ?? DEFAULT_LANGUAGE
        const country = locale.split('-')[1] || 'US'

        const oldKeys = [key, `${country}:${key}`, `${locale}:${key}`]

        oldKeys.forEach(oldKey => {
          if (oldKey !== storageKey) {
            localStorage.removeItem(oldKey)
          }
        })
      }
    } catch (error) {
      console.error('Error setting item in storage:', error)
    }
  }

  /**
   * Convenience method for storing JSON objects
   * @param key - The key to store
   * @param value - The object to store (will be JSON.stringify'd)
   * @param options - Storage options (optional)
   * @param options.isGlobal - true: use original key, false: use localized key format (default: true)
   * @param options.includeLangInKey - false: use country prefix (e.g., "US:key"), true: use full locale prefix (e.g., "en-us:key") (default: false)
   */
  setItemJSON<T>(key: string, value: T, options: StorageOptions = {}): void {
    try {
      const jsonValue = JSON.stringify(value)
      this.setItem(key, jsonValue, options)
    } catch (error) {
      console.error('Error serializing value to JSON:', error)
    }
  }

  /**
   * Convenience method for getting JSON objects with automatic parsing
   * @param key - The key to retrieve
   * @param options - Storage options (optional)
   * @param options.isGlobal - true: use original key, false: use localized key format (default: true)
   * @param options.includeLangInKey - false: use country prefix (e.g., "US:key"), true: use full locale prefix (e.g., "en-us:key") (default: false)
   * @returns Parsed object or null if not found/invalid JSON
   */
  getItemJSON<T>(key: string, options: StorageOptions = {}): T | null {
    const item = this.getItem<string>(key, options)
    if (!item) return null

    try {
      return JSON.parse(item) as T
    } catch (error) {
      console.error('Error parsing JSON from storage:', error)
      return null
    }
  }

  /**
   * Removes an item from storage
   * @param key - The key to remove
   * @param options - Storage options (optional)
   * @param options.isGlobal - true: use original key, false: use localized key format (default: true)
   * @param options.includeLangInKey - false: use country prefix (e.g., "US:key"), true: use full locale prefix (e.g., "en-us:key") (default: false)
   * @param options.removeAllLocalized - true: removes all localized variations of the key (default: false)
   *
   * @example
   * // Remove specific key based on current options
   * storage.removeItem('userPrefs', { isGlobal: false })
   * // Removes: "US:userPrefs" or "CA:userPrefs" based on current locale
   *
   * // Remove all localized variations
   * storage.removeItem('userPrefs', { removeAllLocalized: true })
   * // Removes: "userPrefs", "US:userPrefs", "CA:userPrefs", "en-us:userPrefs", "fr-CA:userPrefs"
   */
  removeItem(key: string, options: StorageOptions = {}): void {
    if (!isBrowser()) return

    try {
      const { removeAllLocalized = false } = options

      if (removeAllLocalized) {
        const keysToRemove = [key]
        const { includeLangInKey = false } = options

        if (includeLangInKey) {
          // Remove all locale-prefixed keys (e.g., "en-us:key", "fr-CA:key")
          SUPPORTED_COUNTRIES.forEach(country => {
            console.log('COUNTRY_LANGUAGES :>> ', COUNTRY_LANGUAGES)
            COUNTRY_LANGUAGES[country].forEach(language => {
              const locale = `${language}-${country.toLowerCase()}`
              keysToRemove.push(`${locale}:${key}`)
            })
            console.log('keysToRemove :>> ', keysToRemove)
          })
        } else {
          // Remove all country-prefixed keys (e.g., "US:key", "CA:key")
          SUPPORTED_COUNTRIES.forEach(country => {
            keysToRemove.push(`${country.toLowerCase()}:${key}`)
          })
        }

        console.log('includeLangInKey :>> ', includeLangInKey)

        keysToRemove.forEach(keyToRemove => {
          localStorage.removeItem(keyToRemove)
        })
      } else {
        const storageKey = this.getLocalizedKey(key, options)
        localStorage.removeItem(storageKey)
      }
    } catch (error) {
      console.error('Error removing item from storage:', error)
    }
  }

  /**
   * Checks if an item exists in storage
   * @param key - The key to check
   * @param options - Storage options (optional)
   * @param options.isGlobal - true: use original key, false: use localized key format (default: true)
   * @param options.includeLangInKey - false: use country prefix (e.g., "US:key"), true: use full locale prefix (e.g., "en-us:key") (default: false)
   * @returns true if the item exists, false otherwise
   */
  hasItem(key: string, options: StorageOptions = {}): boolean {
    if (!isBrowser()) return false

    try {
      const { isGlobal = true } = options
      const storageKey = this.getLocalizedKey(key, options)
      let item = localStorage.getItem(storageKey)

      // If isGlobal is false and we're in US region, add fallback to original key
      if (!item && !isGlobal && this.isI18nEnabled) {
        const locale = getCookie(LOCALE_COOKIE)
        const country = locale?.split('-')[1]

        // Only apply fallback for US region
        if (country && country.toLowerCase() === 'us') {
          item = localStorage.getItem(key)
          if (item) {
            try {
              localStorage.setItem(`us:${key}`, item)
              localStorage.removeItem(key)
            } catch (error) {
              // If migration fails, still return the item
              console.error('Error during migration:', error)
            }
          }
        }
      }

      // Reverse fallback: if i18n is disabled and we don't find the original key, check for US localized key
      if (!item && !this.isI18nEnabled) {
        item = localStorage.getItem(`us:${key}`)
        if (item) {
          try {
            localStorage.setItem(key, item)
            localStorage.removeItem(`us:${key}`)
          } catch (error) {
            // If migration fails, still return the item
            console.error('Error during migration:', error)
          }
        }
      }

      return !!item
    } catch (error) {
      console.error('Error checking item in storage:', error)
      return false
    }
  }
}

// Create a singleton instance
let storageInstance: EnhancedStorage | null = null

export const getStorage = (): EnhancedStorage => {
  storageInstance ??= new EnhancedStorage(shouldEnableI18nClient())
  return storageInstance
}
